<template>
    <!-- Filters -->
    <div class="mb-4 flex gap-4 flex-wrap">
        <div class="flex-1 min-w-[250px]">
            <IconField iconPosition="left">
                <InputIcon class="fa-solid fa-search"></InputIcon>
                <InputText :model-value="search" :placeholder="t('userGroups.searchByName')" class="w-full"
                    @input="$emit('search', ($event.target as HTMLInputElement).value)" />
            </IconField>
        </div>
        <div class="min-w-[200px]">
            <Select :model-value="statusFilter" :options="statusOptions" optionLabel="label" optionValue="value"
                :placeholder="t('status.filterByStatus')" class="w-full"
                @change="$emit('statusChange', $event.value)" />
        </div>
        <Button icon="fa-solid fa-plus" :label="t('userGroups.new')" @click="$emit('create')"
            class="whitespace-nowrap min-w-[140px]" />
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card v-for="n in 6" :key="n">
            <template #content>
                <Skeleton height="200px" />
            </template>
        </Card>
    </div>

    <!-- Empty State -->
    <div v-else-if="userGroups.length === 0" class="text-center py-12 text-gray-500">
        <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-3">
            <i class="fa-regular fa-users text-3xl text-gray-400"></i>
        </div>
        <p class="font-semibold text-gray-700">{{ t('userGroups.noUserGroupsFound') }}</p>
    </div>

    <!-- Cards Grid -->
    <div v-else>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Card v-for="userGroup in userGroups" :key="userGroup.id" class="hover:shadow-lg transition-shadow">
                <template #header>
                    <div class="p-3 bg-primary-50 border-b">
                        <h3 class="text-2xl mb-1 font-bold text-primary-700">{{ userGroup.name }}</h3>
                        <p v-if="userGroup.description" class="text-gray-600 line-clamp-2">
                            {{ userGroup.description }}
                        </p>
                    </div>
                </template>
                <template #content>
                    <div class="space-y-3">
                        <!-- Users -->
                        <div class="flex items-center justify-between">
                            <div class="flex items-center gap-2">
                                <i class="fa-solid fa-users text-gray-500"></i>
                                <span class="font-medium">{{ t('userGroups.users') }}</span>
                            </div>
                            <Button :label="`${userGroup.users?.length || 0}`" icon="fa-solid fa-users"
                                :severity="userGroup.users?.length ? 'info' : 'secondary'" outlined size="small"
                                @click="$emit('showUsers', userGroup)" />
                        </div>

                        <!-- Notifications -->
                        <div class="flex items-center justify-between">
                            <div class="flex items-center gap-2">
                                <i class="fa-regular fa-bell text-gray-500"></i>
                                <span class="font-medium">{{ t('userGroups.notifications') }}</span>
                            </div>
                            <Button :label="`${getNotificationsCount(userGroup.id)}`" icon="fa-regular fa-bell"
                                :severity="getNotificationsCount(userGroup.id) > 0 ? 'success' : 'secondary'" outlined
                                size="small" @click="$emit('showNotifications', userGroup)" />
                        </div>
                    </div>
                </template>
                <template #footer>
                    <div class="flex justify-between items-center pt-1">
                        <Tag :value="userGroup.enabled ? t('common.enabled') : t('common.disabled')"
                            :severity="userGroup.enabled ? 'success' : 'danger'" class="px-3 py-1" />
                        <div class="flex gap-1">
                            <Button icon="fa-solid fa-edit" text rounded severity="primary" size="large"
                                @click="$emit('edit', userGroup)" />
                            <Button icon="fa-regular fa-trash-can" text rounded severity="danger" size="large"
                                @click="$emit('delete', userGroup)" />
                        </div>
                    </div>
                </template>
            </Card>
        </div>

        <!-- Paginator -->
        <Paginator :first="first" :rows="rows" :totalRecords="totalRecords" @page="$emit('page', $event)" class="mt-4"
            style="border: 1px solid var(--surface-border)" />
    </div>
</template>

<script setup lang="ts">
import type { UserGroup } from '@/lib/types/userGroup';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

interface Props {
    userGroups: UserGroup[];
    loading: boolean;
    search: string;
    statusFilter: boolean | null;
    first: number;
    rows: number;
    totalRecords: number;
    notifications: Record<string, any>;
}

const props = defineProps<Props>();

defineEmits<{
    search: [value: string];
    statusChange: [value: boolean | null];
    page: [event: any];
    create: [];
    edit: [userGroup: UserGroup];
    delete: [userGroup: UserGroup];
    showUsers: [userGroup: UserGroup];
    showNotifications: [userGroup: UserGroup];
}>();

const statusOptions = computed(() => [
    { label: t('status.all'), value: null },
    { label: t('common.enabled'), value: true },
    { label: t('common.disabled'), value: false }
]);

function getNotificationsCount(groupId: string): number {
    return Object.values(props.notifications[groupId] || {}).length;
}
</script>
